import { HealthModels, PolicyModels } from '@wz-models';
import { AddressModels } from 'models/address.models';
import { CommonModels } from 'models/common.models';
import { DocumentModels } from 'models/document.models';
import { QuotationModels } from 'models/quotation.models';
import { SignatureModels } from 'models/signature.models';
import { api } from '.';

export interface RopQuoteData {
  quotePublicId: string;
  quoteRequest: RopCreateQuote;
  documents: RopDocument[];
  isThresholdExceeded: boolean;
  signatureOrder: SignatureModels.SignatureOrderDetails;
}

export const PipelineSteps = {
  profile: 'profile',
  pricing: 'pricing',
  quotation: 'quote',
  subscription: 'subscription',
  signature: 'signature',
  confirmation: 'confirmation',
};

export interface Step {
  order: number;
  key: string;
  label: string;
  route: string[];
  disabled: boolean;
  isWithQuoteId: boolean;
}

export interface RopProfileForm {
  effectDate: string;
  assurePrincipal: Person;
  hasPartner: boolean;
  partner: Person;
  childrenCount: number;
  children: Person[];
  level: QuotationModels.FormulaEnum;
  packageId?: PolicyModels.PackageEnum;
  vadInfo: VadInfo;
  doYouHaveComplementary: boolean;
  whatDoYouHave?: RopNeedPartProfileForm;
  whatDoYouWant: RopNeedPartProfileForm;
}

export interface RopNeedPartProfileForm {
  fraisDeSejour: RopNeedValue;
  chirurgien: RopNeedValue;
  forfaitJournalier: RopNeedValue;
  chambreParticuliere: RopNeedValue;
  phoneInternetTele: RopNeedValue;
}

export interface RopNeedValue {
  isSelected: boolean;
  value?: number;
}

export interface VadInfo {
  isVad?: boolean;
  isCallFromProspect?: boolean;
  isRappel?: boolean;
  isBloctel?: boolean;
  toggleBloctel?: boolean;
  toggleVad?: boolean;
  signatureReadyDate?: Date;
  saleType?: HealthModels.SaleTypeEnum;
  tarifier?: string;
}

export interface RopSubscriptionForm {
  bankAccount: RopBankAccount;
}

export interface RopBankAccount {
  iban: string;
  bic: string;
  civility: CommonModels.CivilityEnum;
  firstName: string;
  lastName: string;
  address: AddressModels.Address;
}

export interface RopQuoteSettingsForm {
  formula: QuotationModels.FormulaEnum;
  options: QuotationModels.WarrantyEnum[];
  staggering: QuotationModels.QuittancementEnum;
  fees: number;
  commissionType: QuotationModels.CommissionTypeEnum;
}

export interface RopProspectForm {
  civility: CommonModels.CivilityEnum;
  firstName: string;
  lastName: string;
  address: AddressModels.Address;
  email: string;
  doNotSendEmail: boolean;
  portable: string;
  phone: string;
  family: RopProspectFamily;
  birthPlace: string;
  birthCountry: string;
  isPoliticallyExposed: boolean;
  isEntournagePoliticallyExposed: boolean;
  doCertify: boolean;
}

export interface RopProspectFamily {
  partner: RopProspectPerson;
  children: RopProspectPerson[];
}

export interface RopProspectPerson {
  civility?: CommonModels.CivilityEnum;
  firstName?: string;
  lastName?: string;
  birthDate?: string;
}

export interface RopCreateQuote {
  packageId: PolicyModels.PackageEnum;
  profile: RopProfileForm;
  quoteSettings: RopQuoteSettingsForm;
  prospect: RopProspectForm;
  subscription?: RopSubscriptionForm;
}

/** Pricing */
export interface RopPricingConfig extends api.GetRopPricingConfigResponse {}
export interface RopPricingDetail extends api.GetRopPricingDetailResponse {}

export interface PricingFull extends RopPricingConfig {
  formulas: RopFormulaFull[];
  options: RopOptionFull[];
}

export interface RopOptionFull extends api.RopOptionConfig {
  warranty: QuotationModels.WarrantyEnum;
  price: Price;
}

export interface RopFormulaFull extends api.RopFormulaConfig {
  formula: QuotationModels.FormulaEnum;
  details: RopFormulaDetailFull[];
  price: Price;
}

export interface RopFormulaDetailFull extends api.RopFormulaDetailConfig {
  detail: RopFormulaDetailEnum;
  euro: number;
}

export interface Person {
  birthDate: string;
}

export interface RopDocument {
  documentType: DocumentModels.DocumentType;
  url: string;
}

export enum RopFormulaDetailEnum {
  HospiSuiteMaladie = 1,
  HospiSuiteAccident = 2,
}

export interface Price {
  yearly: number;
  monthly: number;
}

export const allRopFormulas = [
  QuotationModels.FormulaEnum.Rop20,
  QuotationModels.FormulaEnum.Rop30,
  QuotationModels.FormulaEnum.Rop40,
  QuotationModels.FormulaEnum.Rop50,
  QuotationModels.FormulaEnum.Rop60,
];

export enum RopRateWithholdingEnum {
  PR1 = 1,
}
